// ============================================================================
// ExcelTextParsers.js
// Lightweight text-to-mechanic parsers for Skills.description and Items.passive
// ============================================================================

const DAMAGE_TYPE = {
  PHYSICAL: 'Physical',
  MAGIC: 'Magic',
  TRUE: 'True',
};

const CC_TYPE = {
  STUN: 'Stun',
  SLOW: 'Slow',
  SILENCE: 'Silence',
  KNOCKUP: 'Knockup',
  KNOCKBACK: 'Knockback',
  SUPPRESS: 'Suppress',
  FREEZE: 'Freeze',
  PETRIFY: 'Petrify',
  PULL: 'Pull',
  TAUNT: 'Taunt',
  IMMOBILIZE: 'Immobilize',
};

function toNumber(v, fallback = 0) {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
}

function parseSeconds(text) {
  const m = text.match(/(?:for|during)\s*(\d+(?:\.\d+)?)\s*s/i) || text.match(/(\d+(?:\.\d+)?)\s*s/i);
  return m ? toNumber(m[1], 0) : 0;
}

function parsePercent(text) {
  const m = text.match(/(\d+(?:\.\d+)?)\s*%/);
  return m ? toNumber(m[1], 0) / 100 : 0;
}

function parseFlatDamage(text) {
  // Supports "180 magic damage", "bonus magic 45-87", "100 + 30% bonus AD"
  const m =
    text.match(/(?:bonus|deals?)\s*(?:magic|physical)\s*(\d+(?:\.\d+)?)(?:\s*-\s*\d+(?:\.\d+)?)?/i) ||
    text.match(/(?:damage|bonus)\s*(\d+(?:\.\d+)?)(?:\s*-\s*\d+(?:\.\d+)?)?/i) ||
    text.match(/(\d+(?:\.\d+)?)(?:\s*-\s*\d+(?:\.\d+)?)?\s*(?:magic|physical)/i) ||
    text.match(/(?:^|[^\d])(\d+(?:\.\d+)?)(?:\s*\+)/i);
  return m ? toNumber(m[1], 0) : 0;
}

function parseScalingPct(text, statPattern) {
  const m = text.match(new RegExp(`(\\d+(?:\\.\\d+)?)\\s*%\\s*(?:bonus\\s*)?${statPattern}`, 'i'));
  return m ? toNumber(m[1], 0) / 100 : 0;
}

function parseRequiredHits(text) {
  const lower = String(text || '').toLowerCase();
  const m =
    lower.match(/every\s+(\d+)(?:st|nd|rd|th)?\s*(?:aa|auto attack|normal attack|attacks?|hits?)/i) ||
    lower.match(/after\s+(\d+)\s*(?:aa|auto attacks?|normal attacks?|attacks?|hits?)/i);
  if (!m) return 0;
  const hits = toNumber(m[1], 0);
  return hits > 1 ? hits : 0;
}

function withHitRequirement(passiveDef, rawText) {
  if (!passiveDef) return passiveDef;
  if (passiveDef.trigger !== 'ON_HIT') return passiveDef;
  const requiredHits = parseRequiredHits(rawText);
  if (!requiredHits) return passiveDef;
  return { ...passiveDef, requiredHits };
}

export function inferSkillEffectsFromDescription(skill = {}) {
  const description = String(skill.description || '');
  if (!description.trim()) return {};

  const text = description.toLowerCase();
  const inferred = {};

  // Damage type hints in text
  if (text.includes('true damage')) inferred.damageType = DAMAGE_TYPE.TRUE;
  else if (!skill.damage_type && text.includes('magic damage')) inferred.damageType = DAMAGE_TYPE.MAGIC;

  // Hit count (multi-hit)
  if (/\bhits?\s+twice\b/i.test(description)) inferred.damageInstances = 2;
  else if (/\bhits?\s+thrice\b/i.test(description)) inferred.damageInstances = 3;
  else {
    const hitMatch =
      description.match(/\bhits?\s+(\d+)\s+times?\b/i) ||
      description.match(/\b(\d+)\s+hits?\b/i) ||
      description.match(/\b(\d+)\s+times?\b/i);
    if (hitMatch) {
      const hits = toNumber(hitMatch[1], 0);
      if (hits > 1) inferred.damageInstances = hits;
    }
  }

  // CC inference
  const ccMap = [
    { pattern: /\bstun(?:ned|s)?\b/i, ccType: CC_TYPE.STUN },
    { pattern: /\bslow(?:ed|s)?\b/i, ccType: CC_TYPE.SLOW },
    { pattern: /\bsilence(?:d|s)?\b/i, ccType: CC_TYPE.SILENCE },
    { pattern: /\bknock[\s-]?up\b/i, ccType: CC_TYPE.KNOCKUP },
    { pattern: /\bknock[\s-]?back\b/i, ccType: CC_TYPE.KNOCKBACK },
    { pattern: /\bsuppress(?:ed|es)?\b/i, ccType: CC_TYPE.SUPPRESS },
    { pattern: /\bfreez(?:e|es|ed)\b/i, ccType: CC_TYPE.FREEZE },
    { pattern: /\bpetrif(?:y|ies|ied)\b/i, ccType: CC_TYPE.PETRIFY },
    { pattern: /\bpull(?:s|ed)?\b/i, ccType: CC_TYPE.PULL },
    { pattern: /\btaunt(?:s|ed)?\b/i, ccType: CC_TYPE.TAUNT },
    { pattern: /\bimmobiliz(?:e|es|ed)\b/i, ccType: CC_TYPE.IMMOBILIZE },
  ];
  const ccHit = ccMap.find((x) => x.pattern.test(description));
  if (ccHit) {
    inferred.ccType = ccHit.ccType;
    inferred.ccDuration = parseSeconds(description) || (ccHit.ccType === CC_TYPE.SLOW ? 2 : 1);
  }

  // Self buffs from description
  // Example: "Increases damage dealt by 10% for 6 seconds"
  if (/(increase|increases|increased)\s+damage\s+dealt/i.test(description) && /%/.test(description)) {
    const amp = parsePercent(description);
    const dur = parseSeconds(description) || 3;
    if (amp > 0) {
      inferred.grantsBuff = {
        type: 'DAMAGE_AMP',
        value: amp,
        name: 'Description Amp',
        source: skill.name || skill.skill_name || 'Skill Description',
        duration: dur,
      };
    }
  }

  // Untargetable / invulnerable windows
  if (/\buntargetable\b/i.test(description) || /\binvulnerable\b/i.test(description)) {
    inferred.isUntargetable = true;
    inferred.untargetableDuration = parseSeconds(description) || 1;
  }

  // HP-based scaling inference
  // target max HP
  const targetMaxHp = description.match(/(\d+(?:\.\d+)?)\s*%\s*(?:of\s*)?(?:target'?s?\s*)?max\s*hp/i);
  if (targetMaxHp) {
    inferred.targetMaxHpScaling = toNumber(targetMaxHp[1], 0) / 100;
  }
  // target current HP
  const targetCurrentHp = description.match(/(\d+(?:\.\d+)?)\s*%\s*(?:of\s*)?(?:target'?s?\s*)?current\s*hp/i);
  if (targetCurrentHp) {
    inferred.targetCurrentHpScaling = toNumber(targetCurrentHp[1], 0) / 100;
  }
  // target lost HP
  const targetLostHp = description.match(/(\d+(?:\.\d+)?)\s*%\s*(?:of\s*)?(?:target'?s?\s*)?lost\s*hp/i);
  if (targetLostHp) {
    inferred.targetLostHpScaling = toNumber(targetLostHp[1], 0) / 100;
  }
  // self max HP
  const selfMaxHp = description.match(/(\d+(?:\.\d+)?)\s*%\s*(?:of\s*)?(?:own|self|caster)?\s*max\s*hp/i);
  if (selfMaxHp) {
    inferred.hpMaxScaling = toNumber(selfMaxHp[1], 0) / 100;
  }

  // Execute threshold inference
  // e.g. "instantly kills enemies below 8% HP", "execute below 10% hp"
  const executeThreshold = description.match(/(?:execute|instantly\s*kills?|instant\s*kill)[^%]{0,40}(\d+(?:\.\d+)?)\s*%\s*hp/i)
    || description.match(/below\s*(\d+(?:\.\d+)?)\s*%\s*hp[^.]{0,20}(?:execute|instantly\s*kills?)/i)
    || description.match(/if\s*target[^%]{0,30}below\s*(\d+(?:\.\d+)?)\s*%\s*hp/i);
  if (executeThreshold) {
    inferred.executeThresholdPct = toNumber(executeThreshold[1], 0) / 100;
    inferred.executeIsTrueDamage = true;
  }

  return inferred;
}

export function inferItemPassiveFromText(passiveText, itemName = '') {
  const raw = String(passiveText || '').trim();
  if (!raw) return null;

  const text = raw.toLowerCase();
  const name = `Excel Passive${itemName ? ` (${itemName})` : ''}`;

  // Trigger inference
  let trigger = 'ON_HIT';
  if (text.includes('after skill') || text.includes('skill hit') || text.includes('skill damage')) {
    trigger = 'AFTER_SKILL';
  } else if (text.includes('per second') || /\bevery\s+\d+(?:\.\d+)?s\b/i.test(raw)) {
    trigger = 'PERIODIC';
  } else if (!text.includes('attack') && !text.includes('aa')) {
    trigger = 'ALWAYS';
  }

  // Next AA bonus after skill
  if (text.includes('next aa') || text.includes('next normal attack')) {
    const damageType = text.includes('magic') ? DAMAGE_TYPE.MAGIC : DAMAGE_TYPE.PHYSICAL;
    const base = parseFlatDamage(raw);
    const adScaling = parseScalingPct(raw, 'ad');
    const apScaling = parseScalingPct(raw, 'ap');
    const duration = parseSeconds(raw) || 5;

    return {
      name: itemName || name,
      trigger: 'AFTER_SKILL',
      cooldown: 0,
      description: raw,
      nextAABonus: {
        damage: {
          base,
          adScaling,
          apScaling,
          damageType,
        },
      },
      stackDuration: duration,
    };
  }

  // Armor shred stacks
  if (text.includes('reduce target armor') || text.includes('reduce target defence') || text.includes('reduce target defense')) {
    const shredPct = parsePercent(raw) || 0.06;
    const maxStacksMatch = raw.match(/max\s*(\d+)/i);
    const duration = parseSeconds(raw) || 5;
    return withHitRequirement({
      name: itemName || name,
      trigger: 'ON_HIT',
      effect: 'ARMOR_SHRED',
      shredPct,
      maxStacks: maxStacksMatch ? toNumber(maxStacksMatch[1], 4) : 4,
      shredDuration: duration,
      cooldown: 0,
      description: raw,
    }, raw);
  }

  // AS stacks
  if ((text.includes('attack speed') || /\bas\b/.test(text)) && text.includes('stack')) {
    const asPerStack = parsePercent(raw) || 0.1;
    const maxStacksMatch = raw.match(/max\s*(\d+)/i);
    const duration = parseSeconds(raw) || 2;
    return withHitRequirement({
      name: itemName || name,
      trigger: 'ON_HIT',
      effect: 'ATTACK_SPEED_STACK',
      asPerStack,
      maxStacks: maxStacksMatch ? toNumber(maxStacksMatch[1], 5) : 5,
      stackDuration: duration,
      cooldown: 0,
      description: raw,
    }, raw);
  }

  // Slow
  if (text.includes('slow')) {
    return withHitRequirement({
      name: itemName || name,
      trigger: trigger === 'ALWAYS' ? 'AFTER_SKILL' : trigger,
      effect: 'SLOW',
      slowPct: parsePercent(raw) || 0.2,
      slowDuration: parseSeconds(raw) || 2,
      cooldown: 0,
      description: raw,
    }, raw);
  }

  // DoT (magic per second)
  if (text.includes('per second') && (text.includes('dot') || text.includes('damage'))) {
    return withHitRequirement({
      name: itemName || name,
      trigger: trigger === 'ALWAYS' ? 'PERIODIC' : trigger,
      effect: 'APPLY_DOT',
      dotDamage: parseFlatDamage(raw),
      dotDamageType: text.includes('physical') ? DAMAGE_TYPE.PHYSICAL : DAMAGE_TYPE.MAGIC,
      dotDuration: parseSeconds(raw) || 3,
      dotInterval: 10,
      cooldown: 0,
      description: raw,
    }, raw);
  }

  // Bonus damage
  if (text.includes('bonus physical') || (text.includes('physical damage') && (text.includes('aa') || text.includes('attack')))) {
    const targetHPPct = (raw.match(/(\d+(?:\.\d+)?)\s*%\s*of\s*target\s*current\s*hp/i) || [])[1];
    return withHitRequirement({
      name: itemName || name,
      trigger,
      effect: 'BONUS_PHYSICAL_DAMAGE',
      flatDamage: parseFlatDamage(raw),
      targetHPPct: targetHPPct ? toNumber(targetHPPct, 0) / 100 : 0,
      adScaling: parseScalingPct(raw, 'ad'),
      cooldown: (raw.match(/cd\s*(\d+(?:\.\d+)?)/i) || [])[1] ? toNumber(raw.match(/cd\s*(\d+(?:\.\d+)?)/i)[1], 0) : 0,
      description: raw,
    }, raw);
  }

  if (text.includes('bonus magic') || text.includes('magic damage')) {
    const targetMaxHPPct = (raw.match(/(\d+(?:\.\d+)?)\s*%\s*target\s*max\s*hp/i) || [])[1];
    return withHitRequirement({
      name: itemName || name,
      trigger,
      effect: 'BONUS_MAGIC_DAMAGE',
      flatDamage: parseFlatDamage(raw),
      targetMaxHPPct: targetMaxHPPct ? toNumber(targetMaxHPPct, 0) / 100 : 0,
      adScaling: parseScalingPct(raw, 'ad'),
      apScaling: parseScalingPct(raw, 'ap'),
      cooldown: (raw.match(/cd\s*(\d+(?:\.\d+)?)/i) || [])[1] ? toNumber(raw.match(/cd\s*(\d+(?:\.\d+)?)/i)[1], 0) : 0,
      description: raw,
    }, raw);
  }

  // Simple ALWAYS stat passives from text
  // Example: "Armor Pierce +40%", "Magic Pierce +40%", "+15% Life Steal"
  const armorPiercePct = raw.match(/armor\s*pierce\s*\+?\s*(\d+(?:\.\d+)?)\s*%/i);
  if (armorPiercePct) {
    const value = toNumber(armorPiercePct[1], 0) / 100;
    return {
      name: itemName || name,
      trigger: 'ALWAYS',
      description: raw,
      applyToStats(stats) {
        return { ...stats, armor_pierce_pct: (stats.armor_pierce_pct || 0) + value };
      },
      apply() { return null; },
    };
  }

  const magicPiercePct = raw.match(/magic\s*pierce\s*\+?\s*(\d+(?:\.\d+)?)\s*%/i);
  if (magicPiercePct) {
    const value = toNumber(magicPiercePct[1], 0) / 100;
    return {
      name: itemName || name,
      trigger: 'ALWAYS',
      description: raw,
      applyToStats(stats) {
        return { ...stats, magic_pierce_pct: (stats.magic_pierce_pct || 0) + value };
      },
      apply() { return null; },
    };
  }

  const lifeStealPct = raw.match(/life\s*steal\s*\+?\s*(\d+(?:\.\d+)?)\s*%/i);
  if (lifeStealPct) {
    const value = toNumber(lifeStealPct[1], 0) / 100;
    return {
      name: itemName || name,
      trigger: 'ALWAYS',
      description: raw,
      applyToStats(stats) {
        return { ...stats, life_steal: (stats.life_steal || 0) + value };
      },
      apply() { return null; },
    };
  }

  return null;
}
